/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/utils/navigation.ts
 * PURPOSE: Utility functions and helper methods.
 *          2026-08-15: web-sync checklist A.2 — removed the 'Trang chủ' →
 *          'Home' entry from getMobileLabel's shortening table (see inline
 *          comment); it was inert until BottomTabNavigation was wired into
 *          the employee routes in this change.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (Code Reusability)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Utility Functions, Helper Libraries, Vietnamese F&B Utilities
 * - Impact: Code Reusability, Developer Productivity, Maintainability
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Utility Layer, Helper Functions
 * - Pattern: Pure Functions, Utility Patterns, Type-Safe Helpers
 * - Dependencies: TypeScript, Lodash (optional)
 */

import {
  ClockIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CogIcon,
  UsersIcon,
  BuildingOfficeIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';

import { NavigationItem, UserRole } from '@/types/navigation';
import { canAccessFeature, hasPermission, Permission } from '@/utils/permissions';

/**
 * Generate navigation items based on user role with enhanced permissions
 */
export const getNavigationItems = (userRole?: string): NavigationItem[] => {
  // Per NAVIGATION-FLOW-MOCKUP.html lines 677-726 - exact order
  const allItems: NavigationItem[] = [
    // 1. Trang chủ (Home)
    {
      id: 'home',
      icon: HomeIcon,
      label: 'Trang chủ',
      path: '/',
      tooltip: 'Trang chủ với chấm công nhanh',
    },
    // 2. Dashboard (Manager+ only)
    {
      id: 'dashboard',
      icon: ChartBarIcon,
      label: 'Dashboard',
      path: '/dashboard',
      tooltip: 'Dashboard tổng quan',
      feature: 'analytics'
    },
    // 3. Chấm công
    {
      id: 'attendance',
      icon: ClockIcon,
      label: 'Chấm công',
      path: '/attendance',
      tooltip: 'Chấm công và xem lịch sử',
    },
    // 4. Lịch làm việc
    {
      id: 'schedule',
      icon: CalendarIcon,
      label: 'Lịch làm việc',
      path: '/schedule',
      tooltip: 'Xem và quản lý lịch làm việc',
    },
    // 5. Nhiệm vụ (Công việc)
    {
      id: 'tasks',
      icon: ClipboardDocumentListIcon,
      label: 'Nhiệm vụ',
      path: '/tasks',
      tooltip: 'Quản lý công việc được giao',
    },
    // 6. Ca Gãy - DOC-GAP-003: Split Shift navigation entry (PM/PJM Decision Jan 6, 2026)
    // Manager+ only - displays split shift summary with allowance calculations
    {
      id: 'split-shift',
      icon: ClockIcon,
      label: 'Ca Gãy',
      path: '/compliance/split-shift',
      tooltip: 'Xem tổng hợp ca gãy và phụ cấp',
      feature: 'split-shift'
    },
    // 7. Nhân viên (Team management - Supervisor+)
    {
      id: 'users',
      icon: UsersIcon,
      label: 'Nhân viên',
      path: '/users',
      tooltip: 'Quản lý nhân viên và nhóm',
      feature: 'users'
    },
    // 8. Báo cáo (Manager features)
    {
      id: 'reports',
      icon: ChartBarIcon,
      label: 'Báo cáo',
      path: '/reports',
      tooltip: 'Tạo và xuất báo cáo',
      feature: 'reports'
    },
    // 9. Tổ chức (Admin features)
    {
      id: 'organizations',
      icon: BuildingOfficeIcon,
      label: 'Tổ chức',
      path: '/organizations',
      tooltip: 'Quản lý tổ chức và chi nhánh',
      feature: 'organizations'
    },
    // 10. Cài đặt
    {
      id: 'settings',
      icon: CogIcon,
      label: 'Cài đặt',
      path: '/settings',
      tooltip: 'Cấu hình hệ thống',
      feature: 'settings'
    },

    // Platform Administration (SUPER_ADMIN only)
    {
      id: 'platform-management',
      icon: BuildingOfficeIcon,
      label: 'Quản lý nền tảng',
      path: '/platform',
      tooltip: 'Quản lý toàn bộ nền tảng và khách hàng',
      requiredPermission: Permission.MANAGE_TENANTS
    },
    {
      id: 'platform-analytics',
      icon: ChartBarIcon,
      label: 'Phân tích nền tảng',
      path: '/platform/analytics',
      tooltip: 'Phân tích hiệu suất toàn nền tảng',
      requiredPermission: Permission.PLATFORM_ANALYTICS
    },
    {
      id: 'platform-billing',
      icon: CogIcon,
      label: 'Thanh toán nền tảng',
      path: '/platform/billing',
      tooltip: 'Quản lý thanh toán và đăng ký',
      requiredPermission: Permission.PLATFORM_BILLING
    }
  ];

  // Filter items based on user role and permissions
  return allItems.filter(item => {
    // Check specific permission requirement (for platform features)
    if (item.requiredPermission) {
      return hasPermission(userRole, item.requiredPermission);
    }
    
    // Check feature requirement (for general features)
    if (item.feature) {
      return canAccessFeature(userRole, item.feature);
    }
    
    // If no requirements, allow all users
    return true;
  });
};

/**
 * Get optimized navigation items for mobile (max 5 items)
 */
export const getMobileNavigationItems = (items: NavigationItem[]): NavigationItem[] => {
  // Priority order for mobile navigation
  const priorityOrder = [
    '/', // Home/Clock (highest priority)
    '/schedule', // Schedule
    '/tasks', // Tasks
    '/team', // Team management
    '/ai-dashboard', // Analytics
  ];

  // BUGFIX: Use [...items] to avoid mutating the original array
  // Array.sort() mutates in place, which was reordering the desktop navigation
  const sorted = [...items].sort((a, b) => {
    const aIndex = priorityOrder.indexOf(a.path);
    const bIndex = priorityOrder.indexOf(b.path);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  // Max 5 items for mobile bottom tabs
  return sorted.slice(0, 5);
};

/**
 * Check if navigation item should be shown for current user role
 */
export const shouldShowInNavigation = (
  item: NavigationItem, 
  userRole?: UserRole
): boolean => {
  if (!item.requiredRole || item.requiredRole.length === 0) {
    return true;
  }
  
  if (!userRole) {
    return false;
  }
  
  return item.requiredRole.includes(userRole);
};

/**
 * Filter navigation items based on user role and permissions
 */
export const filterNavigationItems = (
  items: NavigationItem[],
  userRole?: UserRole
): NavigationItem[] => {
  return items.filter(item => shouldShowInNavigation(item, userRole));
};

/**
 * Get navigation item by path
 */
export const getNavigationItemByPath = (
  items: NavigationItem[],
  path: string
): NavigationItem | undefined => {
  return items.find(item => item.path === path);
};

/**
 * Get navigation label by path
 */
export const getNavigationLabelByPath = (
  items: NavigationItem[],
  path: string
): string => {
  const item = getNavigationItemByPath(items, path);
  return item?.label || 'Không xác định';
};

/**
 * Generate breadcrumb items for current path
 */
export const generateBreadcrumbs = (
  currentPath: string,
  navigationItems: NavigationItem[]
) => {
  const breadcrumbs = [
    { label: 'Trang chủ', path: '/', isActive: false }
  ];

  // If not home page, add current page
  if (currentPath !== '/') {
    const currentItem = getNavigationItemByPath(navigationItems, currentPath);
    if (currentItem) {
      breadcrumbs.push({
        label: currentItem.label,
        path: currentPath,
        isActive: true
      });
    }
  } else {
    breadcrumbs[0].isActive = true;
  }

  return breadcrumbs;
};

/**
 * Get default landing page based on user role
 */
export const getDefaultLandingPage = (userRole?: UserRole): string => {
  switch (userRole) {
    case 'admin':
    case 'manager':
      return '/manager'; // Manager dashboard with analytics
    case 'supervisor':
      return '/team'; // Team management dashboard
    case 'employee':
    default:
      return '/'; // Clock in/out page (MagicClock)
  }
};

/**
 * Get navigation items with badge counts (for notifications, etc.)
 */
export const addNavigationBadges = (
  items: NavigationItem[],
  badges: Record<string, number>
): NavigationItem[] => {
  return items.map(item => ({
    ...item,
    badge: badges[item.id || item.path] || item.badge
  }));
};

/**
 * Check if current path matches navigation item
 */
export const isNavigationItemActive = (
  item: NavigationItem,
  currentPath: string
): boolean => {
  if (item.path === currentPath) {
    return true;
  }
  
  // Check for child routes (e.g., /tasks matches /tasks/123)
  if (currentPath.startsWith(item.path + '/')) {
    return true;
  }
  
  return false;
};

/**
 * Mobile-specific navigation utilities
 */
export const mobileNavigationUtils = {
  /**
   * Check if current screen size is mobile
   */
  isMobile: (): boolean => {
    return window.innerWidth < 768;
  },

  /**
   * Get mobile-optimized labels (shorter versions)
   */
  getMobileLabel: (label: string): string => {
    // web-sync checklist A.2 (2026-08-15): removed 'Trang chủ' → 'Home'.
    // Every other entry here shortens a long Vietnamese label to a shorter
    // Vietnamese one; this lone VN→EN mapping was dead code (BottomTabNavigation,
    // its only caller, was not wired into any route until this change) and
    // would have silently rendered "Home" on an otherwise all-Vietnamese
    // employee bottom nav — a Vietnamese-localization regression.
    const mobileLabels: Record<string, string> = {
      'Lịch làm việc': 'Lịch',
      'Công việc': 'Việc',
      'Quản lý nhóm': 'Nhóm',
      'Phân tích AI': 'AI',
      'BPM Workflows': 'BPM',
      'Quản lý tổ chức': 'Tổ chức',
    };
    
    return mobileLabels[label] || label;
  },

  /**
   * Check if navigation item is suitable for mobile
   */
  isMobileOptimized: (item: NavigationItem): boolean => {
    // Some features might be desktop-only
    const desktopOnlyPaths = ['/organization'];
    return !desktopOnlyPaths.includes(item.path);
  }
};

/**
 * Constants for navigation configuration
 */
export const navigationConfig = {
  // Maximum items for mobile bottom navigation
  MAX_MOBILE_ITEMS: 5,
  
  // Responsive breakpoints
  MOBILE_BREAKPOINT: 768,
  TABLET_BREAKPOINT: 1024,
  
  // Animation durations
  NAVIGATION_TRANSITION_DURATION: 300,
  
  // Z-index layers
  Z_INDEX: {
    MOBILE_OVERLAY: 50,
    MOBILE_BACKDROP: 40,
    DESKTOP_SIDEBAR: 30,
    TOP_NAV: 20,
  }
};